import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { LISTING_SEARCH_KEY } from '@steelbuy/data-access';
import { AddressCollectionDataProvider, ListingBuyerEntityDataProvider } from '@steelbuy/data-provider';
import { ErrorHandler, NotFoundError } from '@steelbuy/error';

import { SuccessErrorNotificationContextProvider } from '@steelbuy/ui-primitive';
import { ListingDetails } from '../../components/listing-details/ListingDetails';
import { ListingNotFound } from '../../components/listing-details/ListingNotFound';
import { RoutePath } from '../../router/Routes';

type ListingDetailViewProps = {
    checkoutPath: RoutePath;
    goBackPath: RoutePath;
    offerSuccessPath: RoutePath;
};

export const ListingDetailView = ({ checkoutPath, goBackPath, offerSuccessPath }: ListingDetailViewProps) => {
    const { listingId = null } = useParams();
    const queryClient = useQueryClient();

    const { t } = useTranslation('translation');

    useEffect(
        () => () => {
            queryClient.removeQueries({ queryKey: [LISTING_SEARCH_KEY] });
        },
        []
    );

    if (listingId === null) {
        throw new NotFoundError(t('application.listingNotFound.notFoundMessage'));
    }

    const handleError = (error: Error) => {
        if (error instanceof NotFoundError) {
            return <ListingNotFound />;
        }
        return null;
    };

    return (
        <ErrorHandler errorComponent={handleError}>
            <SuccessErrorNotificationContextProvider stayOpen>
                <AddressCollectionDataProvider>
                    <ListingBuyerEntityDataProvider entityId={listingId} alwaysFetchOnMount>
                        <ListingDetails
                            checkoutPath={checkoutPath}
                            goBackPath={goBackPath}
                            offerSuccessPath={offerSuccessPath}
                        />
                    </ListingBuyerEntityDataProvider>
                </AddressCollectionDataProvider>
            </SuccessErrorNotificationContextProvider>
        </ErrorHandler>
    );
};
